import {
  Card,
  CardBody,
  DataBlock,
  Table,
  TableCell,
  TableRow,
  TableRowHeader,
  Button,
  TableColumnHeader,
  ProgressBar
} from '@/common'
import { EmissionReportStatus } from '@/api/EmissionReportModel'
import useI18n from '@/shared/hooks/useI18n'
import { ReportCalculatedEmissions } from '@/shared/api/types'
import { RouteProductionMethod } from '@/features/production-routes/api/ProductionRouteModel'
import LocalizedNumberFlow from '@/components/LocalizednNumberFlow'
import { UnitLabel } from '@/shared/config/config'
import { usePermissions } from '@/shared/hooks/usePermissions'
import CnCodeTag from '@/components/CnCodeTag'

export default function IntegratedEmissionReportStatusControlSection({
  reportStatus,
  onSubmit,
  isSubmitting,
  startDate,
  endDate,
  calculatedEmissionsForAllMethods,
  productionRouteName,
  productionMethods,
  methodPercentages
}: {
  reportStatus: EmissionReportStatus
  onSubmit: () => void
  isSubmitting: boolean
  startDate: string
  endDate: string
  calculatedEmissionsForAllMethods?: ReportCalculatedEmissions
  productionRouteName: string
  productionMethods: RouteProductionMethod[]
  methodPercentages: number[]
}) {
  const { t, dateFormatter } = useI18n()
  const { checkPermission } = usePermissions()
  const canEditReport = checkPermission('emissionReport', 'update')

  const formattedStartDate = dateFormatter(new Date(startDate))
  const formattedEndDate = dateFormatter(new Date(endDate))

  const areAllMethodsComplete = methodPercentages.every(percentage => percentage === 100)
  return (
    <Card>
      <CardBody>
        <div className="space-y-4">
          <h4 className="mb-1 font-semibold text-sm">{productionRouteName}</h4>
          <DataBlock
            label={t('report:emissionReportStatusControl.reportingPeriod')}
            data={`${formattedStartDate} - ${formattedEndDate}`}
          />
          <Table>
            <thead>
              <TableRow>
                <TableColumnHeader>
                  {t('report:emissionReportStatusControl.integratedMethodsTable.headers.method')}
                </TableColumnHeader>
                <TableColumnHeader>
                  {t('report:emissionReportStatusControl.integratedMethodsTable.headers.cnCodes')}
                </TableColumnHeader>
                <TableColumnHeader>
                  {t(
                    'report:emissionReportStatusControl.integratedMethodsTable.headers.completion'
                  )}
                </TableColumnHeader>
                <TableColumnHeader>
                  {t(
                    'report:emissionReportStatusControl.integratedMethodsTable.headers.totalProduct'
                  )}{' '}
                  ({UnitLabel.t})
                </TableColumnHeader>
                <TableColumnHeader>
                  {t('report:emissionReportStatusControl.integratedMethodsTable.headers.direct')} (
                  {UnitLabel.tCO2})
                </TableColumnHeader>
                <TableColumnHeader>
                  {t('report:emissionReportStatusControl.integratedMethodsTable.headers.indirect')}{' '}
                  ({UnitLabel.tCO2})
                </TableColumnHeader>
              </TableRow>
            </thead>
            <tbody>
              {productionMethods?.map((method, index) => (
                <TableRow key={method.code}>
                  <TableRowHeader className="w-40">
                    {t(`common:productionMethods.${method.code}`)}
                  </TableRowHeader>
                  <TableCell>
                    <div className="flex space-x-4 items-center">
                      {method.cnCodes?.map(cnCode => {
                        return (
                          <div key={cnCode}>
                            <CnCodeTag cnCode={cnCode} />
                          </div>
                        )
                      })}
                    </div>
                  </TableCell>
                  <TableCell className="w-24">
                    <ProgressBar value={methodPercentages[index]} maxValue={100} />
                  </TableCell>
                  <TableCell className="w-32">
                    <LocalizedNumberFlow
                      value={calculatedEmissionsForAllMethods?.[index]?.activityLevel ?? 0}
                    />
                  </TableCell>
                  <TableCell className="w-32">
                    <LocalizedNumberFlow
                      value={calculatedEmissionsForAllMethods?.[index]?.totalDirectEmissions ?? 0}
                    />
                  </TableCell>
                  <TableCell className="w-32">
                    <LocalizedNumberFlow
                      value={calculatedEmissionsForAllMethods?.[index]?.totalIndirectEmissions ?? 0}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>

          <div className="flex justify-end">
            <Button
              disabled={
                !areAllMethodsComplete ||
                reportStatus === EmissionReportStatus.SUBMITTED ||
                isSubmitting ||
                !canEditReport
              }
              className="min-w-32"
              onClick={onSubmit}
              isLoading={isSubmitting}
            >
              {t('report:emissionReportStatusControl.submitReport')}
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  )
}
